// ─────────────────────────────────────────────────────────────────────────
// Information Architecture — 5 child-facing pillars + 1 separated adult hub
// ─────────────────────────────────────────────────────────────────────────
//
// This is the SINGLE source of truth for the app's post-redesign IA.
// The homepage tiles, the bottom nav, the "More" menu, and each pillar's
// landing page all import from here so a change in one place propagates
// everywhere.
//
// Colors align with the existing Home tile palette so returning users don't
// re-learn wayfinding. The custom hex tokens from the spec map to Tailwind
// utilities as follows:
//
//   explore-yellow  #F5C542 → bg-yellow-300
//   play-green      #7FC97F → bg-emerald-300
//   create-orange   #F0967A → bg-orange-300
//   adventure-teal  #4FBDBD → bg-teal-300
//   myworld-purple  #9B8FD9 → bg-violet-300
//
// De-duplication rule (spec section 2.3): Countries/Continents data lives
// ONCE in /data/countries.js. Each pillar merely presents it in a different
// interaction mode:
//   Explore   → reference/learning mode (facts, flags, landmarks)
//   Adventure → narrative/quest mode (Country Journeys wrap the same data)
//   Play      → challenge mode (Geo Challenges quiz the same data)

import {
  Compass, Gamepad2, Palette, Rocket, User,
} from "lucide-react";

export const PILLARS = [
  {
    key: "explore",
    to: "/explore",
    label: "Explore",
    Icon: Compass,
    // Kid-friendly one-liner shown under the label on the home tile.
    tagline: "Atlas · Landmarks · Cultures",
    bg: "bg-yellow-300",
    // Slightly lighter theme colour used by the pillar landing page hero.
    theme: "from-yellow-200 via-amber-200 to-orange-200",
    // Text style for the pillar tile.
    text: "text-slate-900",
    // Child items. Each entry either points at a real route or a stub
    // (soon: true) that renders a friendly "Coming Soon" placeholder card.
    items: [
      { to: "/continents",      label: "Atlas",         emoji: "🌍" },
      { to: "/coloring",        label: "Colour a landmark", emoji: "🗼" },
      { to: "/geo-lines",       label: "Latitude & Longitude",   emoji: "🧭" },
      { to: "/globe",           label: "Maps",          emoji: "🌐" },
      { to: "/explore/nature",  label: "Nature",        emoji: "🌿" },
      { to: "/explore/animals", label: "Animals",       emoji: "🦁" },
      { to: "/explore/wonders", label: "World Wonders", emoji: "🏛️" },
      { to: "/explore/cultures",label: "Cultures",      emoji: "🎭" },
      { to: "/languages",       label: "Languages",     emoji: "🗣️" },
    ],
  },
  {
    key: "play",
    to: "/play",
    label: "Play",
    Icon: Gamepad2,
    tagline: "Games · quizzes · puzzles",
    bg: "bg-emerald-300",
    theme: "from-emerald-200 via-lime-200 to-sky-200",
    text: "text-slate-900",
    // Jul 2026: Play landing gained the canonical Treasure Quest entry
    // + the "quick play" games (Spelling Bee, Tic Tac Toe, Name-Place-
    // Animal) so a kid on the Play pillar sees the full game library
    // rather than only the tier-strip picks.
    items: [
      { to: "/games",             label: "Mini Games",       emoji: "🎮" },
      { to: "/quiz",              label: "Quizzes",          emoji: "🧠" },
      { to: "/puzzles",           label: "Puzzles",          emoji: "🧩" },
      { to: "/treasure-quest",    label: "Treasure Quest",   emoji: "💰" },
      { to: "/maths-academy",     label: "Maths Academy",    emoji: "🎓" },
      { to: "/math",              label: "Math Challenges",  emoji: "🧮" },
      { to: "/spell",             label: "Geo Challenges",   emoji: "✍️" },
      { to: "/snap",              label: "Memory Games",     emoji: "⚡" },
      { to: "/squishy",           label: "Speed Challenges", emoji: "🟢" },
      { to: "/knowledge",         label: "Knowledge Quests", emoji: "📚" },
      { to: "/spelling-bee",      label: "Spelling Bee",     emoji: "🐝" },
      { to: "/tic-tac-toe",       label: "Tic Tac Toe",      emoji: "❌" },
      { to: "/name-place-animal", label: "Name Place Animal",emoji: "🎲" },
      { to: "/dodge",             label: "Dodge",            emoji: "🛡️" },
    ],
  },
  {
    key: "create",
    to: "/create",
    label: "Create",
    Icon: Palette,
    tagline: "Color · draw · flags",
    bg: "bg-orange-300",
    theme: "from-orange-200 via-rose-200 to-pink-200",
    text: "text-slate-900",
    // Deduped: Passport lives only in My World; Sticker "Collection"
    // also lives only in My World. Create keeps only the actively-creative
    // surfaces so kids clearly understand it as an art/make-something area.
    //
    // Jul 2026: added Mandala Studio, Stained Glass and Kaleidoscope —
    // three symmetry-based art activities that already have full
    // dedicated pages but weren't previously reachable from the Create
    // pillar. Together they turn Create from "4 tiles" into a proper
    // craft workshop for kids who prefer making over guessing.
    items: [
      { to: "/coloring",            label: "Coloring",     emoji: "🎨" },
      { to: "/mandala",             label: "Mandala",      emoji: "🌀" },
      { to: "/kaleidoscope",        label: "Kaleidoscope", emoji: "🔮" },
      { to: "/stained-glass",       label: "Stained Glass",emoji: "🪟" },
      { to: "/coloring?mode=pixel", label: "Draw",         emoji: "🖌️" },
      { to: "/coloring?mode=flags", label: "Create Flags", emoji: "🚩" },
      { to: "/stickers",            label: "Sticker Book", emoji: "📔" },
    ],
  },
  {
    key: "adventure",
    to: "/adventure",
    label: "Adventure",
    Icon: Rocket,
    tagline: "Journeys · quests · trips",
    bg: "bg-teal-300",
    theme: "from-teal-200 via-cyan-200 to-sky-200",
    text: "text-slate-900",
    // Jul 2026: Adventure filled out from 3 → 8 tiles so it matches
    // Create + Play in richness. Every route below already exists in
    // App.js — they were just not surfaced from the pillar landing.
    // Ordered from "start now" (FirstQuest) → "explore" (Globe /
    // Continents / Atlas) → "campaign / dream / group" (Campaigns,
    // Dream Trip, Live Quest) → "story" (Country Journeys).
    items: [
      { to: "/first-quest", label: "First Quest",       emoji: "🚀" },
      { to: "/globe",       label: "3D Globe",          emoji: "🌐" },
      { to: "/continents",  label: "Continent Maps",    emoji: "🗺️" },
      { to: "/for-kids-in",  label: "Country Atlas",     emoji: "🧭" },
      { to: "/campaigns",   label: "Campaigns",         emoji: "🏅" },
      { to: "/dream-trip",  label: "Dream Trips",       emoji: "✈️" },
      { to: "/live",        label: "Live Quest",        emoji: "📡" },
      { to: "/learn",       label: "Country Journeys",  emoji: "🧳" },
    ],
  },
  {
    // Cycle 13.6 (Feb 2026) — pillar key renamed to `my-learning`
    // to match the tab's human-facing "My Learning" label. The URL
    // path (`to: "/my-world"`) is intentionally unchanged so
    // bookmarks and PWA cache entries keep working.
    key: "my-learning",
    to: "/my-world",
    label: "My Journey",
    Icon: User,
    tagline: "Profile · progress · rewards",
    bg: "bg-violet-300",
    theme: "from-violet-200 via-purple-200 to-fuchsia-200",
    text: "text-slate-900",
    items: [
      { to: "/passport",             label: "Passport",         emoji: "📒" },
      { to: "/advanced",             label: "Achievements",     emoji: "🏆" },
      { to: "/stickers",             label: "Collection",       emoji: "📔" },
      { to: "/my-world/statistics",  label: "Statistics",       emoji: "📊" },
      { to: "/my-world/history",     label: "Explorer History", emoji: "🕰️" },
    ],
  },
];

// Adult hub — NOT in the bottom nav. Accessed via a small icon in the
// top-right header. Kids can still reach it, but it's clearly separated
// from their day-to-day pillars.
export const ADULT_HUB = {
  key: "grown-ups",
  to: "/parents",
  label: "Parents & Teachers",
  Icon: User,
  bg: "bg-slate-200",
  theme: "from-slate-100 via-gray-100 to-white",
  items: [
    { to: "/parents",  label: "Parent Dashboard",   emoji: "👨‍👩‍👧" },
    { to: "/parents",  label: "Teacher Dashboard",  emoji: "🧑‍🏫" },
    { to: "/parents",  label: "Progress & Reports", emoji: "📈" },
    { to: "/parents",  label: "Accessibility",      emoji: "♿" },
  ],
};

// Convenience: build a fast lookup for the bottom nav / active-highlighting.
export const PILLAR_BY_KEY = Object.fromEntries(PILLARS.map((p) => [p.key, p]));

// Given a current pathname, return the pillar that "owns" it. Used by
// bottom-nav highlighting so any nested route (`/country/france`,
// `/hub/learn`, etc.) still lights up the correct pillar tab.
export function pillarForPath(path) {
  if (!path) return null;
  // Direct pillar landing pages first.
  const direct = PILLARS.find((p) => path === p.to || path.startsWith(`${p.to}/`));
  if (direct) return direct;
  // Otherwise map by sub-route heuristics.
  const map = {
    "/learn": "explore", "/continents": "explore", "/globe": "explore",
    "/geo-lines": "explore", "/country": "explore", "/language": "explore",
    "/games": "play", "/quiz": "play", "/puzzles": "play", "/math": "play",
    "/spell": "play", "/snap": "play", "/squishy": "play",
    "/knowledge": "play", "/maths-academy": "play",
    "/coloring": "create", "/stickers": "create",
    "/mandala": "create", "/stained-glass": "create", "/kaleidoscope": "create",
    "/pixel-art": "create",
    "/campaigns": "adventure", "/dream-trip": "adventure",
    "/ask": "adventure", "/adventure": "adventure",
    "/first-quest": "adventure", "/countries": "adventure",
    "/live": "adventure",
    "/passport": "my-learning", "/advanced": "my-learning",
    "/treasure-quest": "play", "/spelling-bee": "play",
    "/tic-tac-toe": "play", "/name-place-animal": "play",
  };
  for (const prefix of Object.keys(map)) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return PILLAR_BY_KEY[map[prefix]];
    }
  }
  return null;
}
